import { useEffect, useState, useCallback } from "react";
import { Backdrop, Button, Divider, LogoLoader, NewTableAdv, showToast } from "../../../../components";
import { mapTableRows } from "../../../../utils";
import axiosRequest from "../../../../redux/slices/universal/axiosService";
import { ENDPOINTS } from "../../../../redux/apiConfig";
import { dateFormator, exactUTCDate } from "../../../../utils/dateFormator";
import IncomingPlacementDetails from "./IncomingPlacementDetails";
import { useLoginUser } from "../../../../hooks/useLoginUser";
import { updateTotalCountToStore } from "../../../../utils/wf-notifications";

const IncomingPlacementsTab = ({ totalCount }) => {
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const [placementData, setPlacementData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlacement, setSelectedPlacement] = useState(null);

  // Local safe date formatting to avoid "undefined/undefined/..."
  const safeDateFormat = (value) => {
    if (!value || value === "-" || value === "Invalid Date") return "-";
    // If it's already DD-MM-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(value)) return value;
    // If it's YYYY-MM-DD, convert to MM-DD-YYYY for dateFormator or just DD-MM-YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const parts = value.split("-");
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return dateFormator(value, "date-month-year") || "-";
  };
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [unreadPlacementIds, setUnreadPlacementIds] = useState(new Set());

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axiosRequest({
        method: "GET",
        url: ENDPOINTS.INCOMING_PLACEMENTS.GET_ALL,
        params: {
          coll: "IncomingPlacement",
          lim: limit,
          pg: page,
          sort: JSON.stringify({ createdAt: -1 }),
        },
      });

      if (res?.success) {
        setPlacementData(res.data?.data || []);
      } else {
        showToast("Failed to fetch incoming placements", "error");
      }
    } catch (error) {
      console.error("Error fetching incoming placements:", error);
      showToast("Error loading incoming placements", "error");
    } finally {
      setIsLoading(false);
    }
  }, [page, limit]);

  // Fetch unread notification IDs for incoming placements
  const fetchUnreadIds = useCallback(async () => {
    if (!loginUserUiid) return;
    try {
      const res = await axiosRequest({
        method: "GET",
        url: ENDPOINTS.WF_NOTIFICATIONS.GET_ALL,
        params: {
          coll: "Notification",
          lim: 100,
          pg: 1,
          ret: "meta",
          fil: JSON.stringify({
            userId: { type: "NONE", value: loginUserUiid },
            "meta.entity": { type: "NONE", value: "IncomingPlacement" },
            "isRead.status": { type: "NONE", value: false },
          }),
        },
      });
      const ids = new Set(
        (res?.data?.data || []).map((n) => n.meta?.id).filter(Boolean)
      );
      setUnreadPlacementIds(ids);
    } catch (error) {
      console.error("Error fetching unread notification IDs:", error);
    }
  }, [loginUserUiid]);

  useEffect(() => {
    fetchData();
    fetchUnreadIds();
  }, [fetchData, fetchUnreadIds, totalCount]);

  // Mark the corresponding Notification as read for this placement
  const markNotificationAsRead = async (placementUiid) => {
    if (!loginUserUiid || !placementUiid) return;
    try {
      // Direct PATCH using filter — Notification model has no uiid field,
      // so we match by userId + meta fields instead
      await axiosRequest({
        method: "PATCH",
        url: ENDPOINTS.WF_NOTIFICATIONS.UPDATE,
        params: {
          coll: "Notification",
          fil: JSON.stringify({
            userId: loginUserUiid,
            "meta.entity": "IncomingPlacement",
            "meta.id": placementUiid,
            "isRead.status": false,
          }),
        },
        data: {
          payload: {
            markRead: {
              type: "SET",
              value: {
                isRead: {
                  status: true,
                  timestamp: new Date().toISOString(),
                },
              },
            },
          },
        },
      });

      // Refresh the header badge count
      updateTotalCountToStore({ userId: loginUserUiid });
    } catch (error) {
      // Notification might not exist (e.g., already read) — that's OK
      console.error("Error marking notification as read:", error);
    }
  };

  const handleViewDetails = (row) => {
    setSelectedPlacement(row);
    markNotificationAsRead(row?.uiid);
    // Remove from unread set immediately for instant UI feedback
    setUnreadPlacementIds((prev) => {
      const next = new Set(prev);
      next.delete(row?.uiid);
      return next;
    });
  };

  const colDetails = [
    {
      key: "clientName",
      label: "Client Name",
      render: (row) => (
        <div className="text-wrap font-medium">{row?.client?.legalBusinessName || "-"}</div>
      ),
      // Highlight the entire row if this placement has an unread notification
      getRowClass: (row) =>
        unreadPlacementIds.has(row?.uiid)
          ? "bg-primary-50 font-semibold"
          : "",
    },
    {
      key: "employeeName",
      label: "Employee Name",
      render: (row) => (
        <div className="text-wrap">{row?.employee?.firstName} {row?.employee?.lastName}</div>
      ),
    },
    {
      key: "jobTitle",
      label: "Job Title",
      render: (row) => <div className="text-wrap">{row?.placement?.jobTitle || "-"}</div>,
    },
    {
      key: "startDate",
      label: "Start Date",
      render: (row) => (
        <div className="text-wrap">
          {row?.placement?.startDate ? safeDateFormat(row.placement.startDate) : "-"}
        </div>
      ),
    },
    {
      key: "offeredSalary",
      label: "Offered Salary",
      render: (row) => {
        const p = row?.placement || {};
        const pType = Number(p.placementType || (row.module === "IT" ? 1 : 2));
        const commType = Number(p.commissionType || 2);
        const bill = Number(p.billRate) || 0;
        const targetAmount = Number(p.targetAmount) || 0;
        const displaySal = pType === 1 ? Number(p.payRate) || 0 : Number(p.offeredSalary || p.offerSalary || p.billRate) || 0;

        return (
          <div className="text-wrap">
            ${displaySal?.toLocaleString() || "0"}
          </div>
        );
      },
    },
    {
      key: "percentage",
      label: "Percentage",
      render: (row) => {
        const p = row?.placement || {};
        const bill = Number(p.billRate) || 0;
        const pay = Number(p.payRate) || 0;
        const targetPercentage = Number(p.targetPercentage || p.percentage) || 0;
        const targetAmount = Number(p.targetAmount) || 0;
        const pType = Number(p.placementType || (row.module === "IT" ? 1 : 2));
        const commType = Number(p.commissionType || 2);

        let displayPct = 0;
        if (pType === 1) {
          // Contract (IT) - Calculate commission % of bill rate
          displayPct = bill > 0 ? Math.round(((bill - pay) / bill) * 100 * 100) / 100 : 0;
        } else {
          // Permanent (Non-IT)
          if (commType === 1) {
            // Flat: Percentage = Target Amount / Salary
            const offSal = Number(p.offeredSalary || p.offerSalary || p.billRate) || 0;
            displayPct = offSal > 0 ? Math.round((targetAmount / offSal) * 100 * 100) / 100 : 0;
          } else {
            // Percentage: Use the direct percentage
            displayPct = targetPercentage;
          }
        }
        return <div className="text-wrap">{displayPct}%</div>;
      },
    },
    {
      key: "commission",
      label: "Commission",
      render: (row) => {
        const p = row?.placement || {};
        const bill = Number(p.billRate) || 0;
        const pay = Number(p.payRate) || 0;
        const targetPercentage = Number(p.targetPercentage ?? p.percentage) || 0;
        const targetAmount = Number(p.targetAmount) || 0;
        const pType = Number(p.placementType || (row.module === "IT" ? 1 : 2));
        const commType = Number(p.commissionType || 2);

        let displayAmt = 0;
        if (pType === 1) {
          // Contract (IT) - Difference between bill and pay
          displayAmt = bill - pay;
        } else {
          // Permanent (Non-IT)
          if (commType === 1) {
            // Flat: Use the direct target amount
            displayAmt = targetAmount;
          } else {
            // Percentage: Percentage of salary
            const offSal = Number(p.offeredSalary || p.offerSalary || p.billRate) || 0;
            displayAmt = (offSal * targetPercentage) / 100;
          }
        }
        return (
          <div className="text-wrap">
            ${Math.round(displayAmt * 100) / 100}
          </div>
        );
      },
    },
    {
      key: "action",
      label: "Action",
      render: (row) => (
        <Button
          type="button"
          title="View Details"
          variant="outlined"
          color="primary"
          size="sm"
          onClick={() => handleViewDetails(row)}
        />
      ),
    },
  ];

  // Detail view (after all hooks)
  if (selectedPlacement) {
    return (
      <IncomingPlacementDetails
        placementData={selectedPlacement}
        onBack={() => {
          setSelectedPlacement(null);
          fetchData();
          fetchUnreadIds();
        }}
      />
    );
  }

  const mappedrows = mapTableRows(placementData, colDetails);

  return (
    <>
      <div className="px-3">
        <NewTableAdv
          columns={colDetails}
          rows={mappedrows}
          tdClassName="!py-4"
          serverPagination={true}
          totalCount={totalCount}
          limit={limit}
          page={page}
          onPageChange={setPage}
          onLimitChange={setLimit}
          toolbarRight={{
            search: true,
            add: [
              {
                component: (
                  <div className="flex items-center px-2">
                    <Divider orientation="vertical" />
                    <Button
                      type="icon"
                      icon={"refresh"}
                      color="black"
                      variant="text"
                      onClick={fetchData}
                    />
                  </div>
                ),
              },
            ],
          }}
        />
      </div>
      <Backdrop isOpen={isLoading}>
        <LogoLoader />
      </Backdrop>
    </>
  );
};

export default IncomingPlacementsTab;

